import { app, authentication } from '@microsoft/teams-js';

const API_BASE = import.meta.env.VITE_API_BASE_URL;
const DEV_TOKEN = import.meta.env.VITE_DEV_TOKEN; // dev-only override

let tokenCache: { value: string; expires: number } | null = null;
let teamsInit: Promise<void> | null = null;

export function initTeams(): Promise<void> {
  if (!teamsInit) teamsInit = app.initialize();
  return teamsInit;
}

/** Where the app is running. The browser pages (MSAL) switch this to 'web'
 *  so links open synchronously in a new tab instead of going through a
 *  teams-js call that can only fail outside Teams — and, being awaited,
 *  would leave the click stack and trip Safari's popup blocker. */
export type HostMode = 'teams' | 'web';
let hostMode: HostMode = 'teams';
export function setHostMode(mode: HostMode): void { hostMode = mode; }
export function getHostMode(): HostMode { return hostMode; }

/** Open a deep link THROUGH the Teams host (app.openLink) so it stays inside
 *  Teams. On mobile this hands the teams.microsoft.com/l/call link to the
 *  native client (opening the in-app dialer) instead of window.open spawning
 *  the browser first. In web mode (and as a fallback) it is a plain new tab. */
export async function openInTeams(url: string): Promise<void> {
  if (hostMode === 'web') {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }
  try {
    await initTeams();
    await app.openLink(url);
  } catch {
    window.open(url, '_blank');
  }
}

// Default token provider: Teams SSO. The /setup browser page swaps this out
// for an MSAL-backed provider via setTokenProvider() so the same API client
// works in either context.
async function teamsTokenProvider(): Promise<string> {
  await initTeams();
  return authentication.getAuthToken();
}

let tokenProvider: () => Promise<string> = teamsTokenProvider;

export function setTokenProvider(provider: () => Promise<string>): void {
  tokenProvider = provider;
  tokenCache = null; // drop any cached Teams token; the new provider has its own caching
}

/** Forget the cached API token (sign-out, switch account). */
export function clearTokenCache(): void {
  tokenCache = null;
}

async function getAuthToken(): Promise<string> {
  if (DEV_TOKEN) return DEV_TOKEN;
  if (tokenCache && tokenCache.expires > Date.now() + 60_000) return tokenCache.value;

  const token = await tokenProvider();
  tokenCache = { value: token, expires: Date.now() + 55 * 60 * 1000 };
  return token;
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: any) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getAuthToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init.headers || {}),
    },
  });
  if (!res.ok) {
    let body: any;
    try { body = await res.json(); } catch {}
    throw new ApiError(res.status, body?.error || res.statusText, body);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

// --- API surface ---

export interface MeResponse {
  userId: string;
  userPrincipalName: string;
  tenantId: string;
  isAdmin: boolean;
  setupComplete: boolean;
  canBootstrap: boolean;
  /** Company address in browser URLs (/<slug>/home), when the admin set one. */
  slug?: string;
}

/** What GET /public/tenant/{slug} returns: enough to brand the sign-in page
 *  and pin the login to the right organisation. */
export interface PublicTenant {
  tenantId: string;
  slug: string;
  appName: string;
  accentColor: string;
  logoBlobUrl: string | null;
}

export interface Branding {
  appName: string;
  tagline: string;
  accentColor: string;
  logoBlobUrl: string | null;
  /** Show call-queue resource accounts in the directory picker.
   *  Default true. Toggling off filters them out of the picker only —
   *  already-assigned queues continue to render in user/group views. */
  includeQueues?: boolean;
  /** Company address in browser URLs; set in Admin → Branding. */
  slug?: string;
}

/** PUT /branding body: `slug: null` clears the web address. */
export type BrandingUpdate = Omit<Partial<Branding>, 'slug'> & { slug?: string | null };

export interface DirectoryUser {
  id: string;
  displayName: string;
  jobTitle?: string;
  department?: string;
  /** Microsoft Entra "Office" attribute — groups the per-location numbers view. */
  officeLocation?: string;
  phoneNumber?: string;
  email?: string;
  /** Only set on /numbers responses for admins: whether this entry is hidden
   *  from the Numbers tab, and why (so admins can curate). Non-admins never
   *  receive hidden entries at all. */
  hidden?: boolean;
  hiddenReason?: 'explicit' | 'rule';
  /** Only set on /numbers responses for admins: the location slug this entry is
   *  grouped under (people → their office's location; queues → assigned location;
   *  undefined for shared queues / unmapped offices). Lets the admin Numbers tab
   *  scope by the same slug the server enforces. */
  ownSlug?: string;
}

/** Call-queue / auto-attendant resource account from Graph. Same shape as
 *  DirectoryUser since they're AAD users with a specific license SKU. */
export type DirectoryQueue = DirectoryUser;

export interface DirectoryContact {
  id: string;
  displayName: string;
  /** Optional short label shown instead of displayName in the call lists. */
  friendlyName?: string;
  phoneNumber: string;
  notes?: string;
}

export interface Group {
  id: string;
  aadGroupId: string;
  displayName: string;
  description?: string;
  contactIds: string[];
  updatedAt?: string;
  orphaned?: boolean;
}

export interface EffectiveEntry {
  contactId: string;
  source: { type: 'group'; id: string; name: string } | { type: 'direct' };
}

export interface PersonalData {
  contacts: Array<{ id: string; displayName: string; friendlyName?: string; phoneNumber: string; notes?: string }>;
  quickDialIds: string[];
}

// ---- Call flows: mirrors backend/src/lib/types.ts CallFlowDoc ----

export type Weekday = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
/** Minutes since local midnight, half-open [start, end), 0..1440. */
export type TimeInterval = [number, number];
/** Business hours per weekday; a missing day means closed all day. */
export type WeeklyHours = Partial<Record<Weekday, TimeInterval[]>>;
export interface HolidayRange { name: string; start: string; end: string }
export interface LocalizedName { nl?: string; en?: string }
export interface Prompt {
  mode: 'audio' | 'tts' | 'none';
  text?: string;
  /** Uploaded asset id (audio store). */
  audioId?: string;
  /** Teams audio-file id from the export, used to auto-link uploads. */
  teamsAudioFileId?: string;
  teamsFileName?: string;
}
export interface QueueAgent { id: string; optIn?: boolean }
export type ExceptionAction =
  | 'disconnect' | 'forward' | 'voicemail' | 'sharedVoicemail'
  | 'redirectPerson' | 'redirectVoiceApp' | 'redirectPhone' | 'none';
export interface QueueException {
  action: ExceptionAction;
  threshold?: number;
  applyTo?: 'allCalls' | 'newCalls';
  targetId?: string;
  sharedVoicemailGroupId?: string;
  prompt?: Prompt;
  transcription?: boolean;
  suppressSystemPrompt?: boolean;
}
export interface QueueCallback {
  enabled: boolean;
  dtmf?: string;
  waitSeconds?: number;
  callsInQueue?: number;
  callToAgentRatio?: number;
  serviceLevelSeconds?: number;
  prompt?: Prompt;
}
export interface QueueMusic { useDefault: boolean; hold?: Prompt; welcome?: Prompt }

export interface AutoAttendantNode {
  id: string; label: string; type: 'autoAttendant';
  greeting?: string; language?: string; dialByName?: boolean;
  /** Direct dial-in number of the resource account fronting this AA, if any. */
  phoneNumber?: string;
  /** Teams identity — the stable key admin overrides survive re-imports by. */
  teamsId?: string;
  displayName?: LocalizedName;
  menu: Array<{ key: string; label: string; targetId: string }>;
  operatorTargetId?: string;
  timezone?: string;
  hours?: WeeklyHours;
  holidays?: HolidayRange[];
  afterHoursTargetId?: string;
  greetingPrompt?: Prompt;
  menuPrompt?: Prompt;
  voice?: string;
}

export interface QueueFlowNode {
  id: string; label: string; type: 'queue';
  routing?: 'roundRobin' | 'longestIdle' | 'attendant' | 'serial';
  /** Direct dial-in number of the resource account fronting this queue, if any. */
  phoneNumber?: string;
  teamsId?: string;
  displayName?: LocalizedName;
  agentsGroupId?: string; agentsGroupName?: string;
  overflowThreshold?: number; overflowTargetId?: string;
  timeoutSeconds?: number; timeoutTargetId?: string;
  directoryQueueId?: string;
  language?: string;
  /** Agents by Entra object id; names resolve from the directory. */
  agents?: QueueAgent[];
  agentGroupIds?: string[];
  presenceBasedRouting?: boolean;
  conferenceMode?: boolean;
  allowOptOut?: boolean;
  agentAlertTime?: number;
  music?: QueueMusic;
  overflow?: QueueException;
  timeout?: QueueException;
  noAgent?: QueueException;
  callback?: QueueCallback;
}

export interface TerminalFlowNode {
  id: string; label: string;
  type: 'voicemail' | 'person' | 'external' | 'disconnect';
  detail?: string;
  teamsId?: string;
  displayName?: LocalizedName;
}

export type CallFlowNode = AutoAttendantNode | QueueFlowNode | TerminalFlowNode;

/** Metadata of an uploaded audio asset (Phase 3 audio store). */
export interface AudioAssetMeta {
  id: string;
  fileName: string;
  contentType: string;
  bytes: number;
  source: 'upload' | 'teams';
  teamsAudioFileId?: string;
  teamsApplicationId?: string;
  uploadedAt: string;
  uploadedBy: string;
}

export interface CallFlow {
  id: string;
  locationName: string;
  mainNumbers: string[];
  timezone?: string;
  schedule: {
    hours: Array<{ days: string; open: string; close: string }>;
    holidays: Array<{ name: string; date?: string }>;
    /** Structured hours of the main number — source of truth for open/closed. */
    weekly?: WeeklyHours;
    holidayRanges?: HolidayRange[];
  };
  openTargetId: string;
  closedTargetId: string;
  nodes: CallFlowNode[];
  viewerGroupIds: string[];
  displayName?: LocalizedName;
  /** Shared-voicemail groups referenced by queue handling. */
  groups?: Array<{ id: string; name?: string; email?: string }>;
  /** Server-managed sync stamp (auto-sync / import). */
  sync?: { lastSyncAt: string; exportedAt?: string; source: 'manual' | 'auto' };
  /** Read-only: audio assets referenced by this location (attached by GET). */
  audio?: AudioAssetMeta[];
  /** Provenance when imported from Teams — re-imports match on
   *  autoAttendantId and keep an admin-overridden locationName. */
  source?: {
    autoAttendantId: string;
    autoAttendantName: string;
    importedAt: string;
    exportedAt?: string;
  };
  updatedAt?: string;
  updatedBy?: string;
}

/** PUT body — the server stamps id/updatedAt/updatedBy itself and never takes
 *  the server-managed fields (sync, audio) from the body. */
export type CallFlowInput = Omit<CallFlow, 'id' | 'updatedAt' | 'updatedBy' | 'sync' | 'audio'>;

export interface GroupMember {
  id: string;
  displayName: string;
  phoneNumber?: string;
  jobTitle?: string;
  email?: string;
}

export interface ImportProposal {
  locationId: string;
  locationName: string;
  existing: boolean;
  nameOverridden: boolean;
  warnings: string[];
  /** Teams config that could not be represented fully — review before saving. */
  lossy: boolean;
  lossyReasons: string[];
  /** Only reached from another menu; already expanded inside its parent. */
  nested: boolean;
  draft: CallFlow;
}

/** An audio file the export script downloaded next to the JSON (-IncludeAudio). */
export interface ExportAudioFile {
  id: string;
  applicationId?: string;
  fileName?: string;
  file?: string;
  bytes?: number;
  sha256?: string;
}

export interface ImportResult {
  proposals: ImportProposal[];
  warnings: string[];
  exportedAt?: string;
  audioFiles: ExportAudioFile[];
}

export interface GroupInfo {
  id: string;
  displayName: string;
  mail?: string;
  kind: 'm365' | 'security' | 'other';
}

/** Mirrors backend/src/lib/types.ts NumbersLocationAccess. Gates which office
 *  locations a non-admin sees in the Numbers tab (admins see all; everyone also
 *  implicitly sees their own office location). */
export interface NumbersLocationAccess {
  id: string;
  name: string;
  officeLocations: string[];
  matchMode: 'exact' | 'prefix';
  viewerGroupIds: string[];
  /** Call-queue resource-account IDs categorized under this location. */
  queueIds?: string[];
  updatedAt?: string;
  updatedBy?: string;
}

/** PUT body — the server takes id from the route and stamps updatedAt/updatedBy. */
export type NumbersLocationAccessInput = Omit<NumbersLocationAccess, 'id' | 'updatedAt' | 'updatedBy'>;

/** Admin-managed, tenant-wide exclusions for the Numbers tab. Mirrors
 *  backend/src/lib/types.ts NumbersHidden (minus id/tenantId). */
export interface NumbersHiddenRules {
  hideAllQueues?: boolean;
  hideNoNumber?: boolean;
  departments?: string[];
  namePatterns?: string[];
  officeLocations?: string[];
}
export interface NumbersHidden {
  /** Explicitly hidden user/queue object IDs. Tenant-wide ("all sites"). */
  hiddenIds: string[];
  /** Force-show overrides (win over a rule, not over an explicit hide). */
  exceptionIds: string[];
  rules: NumbersHiddenRules;
  /** Per-site hides: location slug → object IDs hidden from viewers of that site. */
  siteHiddenIds?: Record<string, string[]>;
  updatedAt?: string;
  updatedBy?: string;
}

/** Admin-managed display-name overrides for the Numbers tab. The original Entra
 *  name stays canonical (shown to admins); end users see the override. Mirrors
 *  backend/src/lib/types.ts NumbersFriendlyNames (minus id/tenantId). */
export interface NumbersFriendlyNames {
  /** Tenant-wide overrides: object id → friendly name. */
  names: Record<string, string>;
  /** Per-site overrides: location slug → { object id → friendly name }. */
  siteNames?: Record<string, Record<string, string>>;
  updatedAt?: string;
  updatedBy?: string;
}

/** Mirrors the TenantRow shape from backend/src/functions/operatorTenants.ts. */
export interface TenantAccessRow {
  tenantId: string;
  status: 'allowed' | 'pending' | 'denied';
  seeded: boolean;
  requestedBy?: string;
  requestCount?: number;
  firstRequestAt?: string;
  lastRequestAt?: string;
  decidedAt?: string;
  decidedBy?: string;
  setupComplete?: boolean;
}

export interface OperatorOverview {
  tenants: TenantAccessRow[];
  stats: { allowed: number; pending: number; denied: number; requesters7d: number };
}

/** Mirrors backend/src/lib/types.ts AuditEntry. */
export interface AuditEntry {
  id: string;
  tenantId: string;
  userId: string;
  userPrincipalName: string;
  action: 'create' | 'update' | 'delete';
  resource: string;
  target: string;
  before?: any;
  after?: any;
  timestamp: string;
}

export interface PresenceEntry { id: string; availability: string; activity?: string }
export type PresenceResponse =
  | { available: true; fetchedAt: string; presences: PresenceEntry[] }
  | { available: false; reason: 'consent' | 'forbidden' | 'error'; consentUrl?: string };

// ---- Auto-sync (nightly refresh from Teams; docs/auto-sync-setup.md)
export type AutoSyncMode = 'dryRun' | 'apply';
export interface AutoSyncRunSummary {
  runId: string;
  mode: AutoSyncMode;
  effectiveMode: AutoSyncMode;
  exportedAt?: string;
  applied: string[];
  wouldApply: string[];
  unchanged: string[];
  held: Array<{ locationId: string; locationName: string; reasons: string[] }>;
  skipped: string[];
  stale: string[];
  missingAudio: Array<{ teamsAudioFileId: string; applicationId: string; fileName?: string }>;
  warnings: string[];
}
export interface AutoSyncConfig {
  tenantId: string;
  enabled: boolean;
  mode: AutoSyncMode;
  hasSecret: boolean;
  secretHint?: string;
  secretCreatedAt?: string;
  consentedBy?: string;
  consentedAt?: string;
  lastRunAt?: string;
  lastRunStatus?: 'ok' | 'error';
  lastRunSummary?: AutoSyncRunSummary;
  runCount: number;
}
export interface AutoSyncPending {
  id: string;
  locationId: string;
  proposal: ImportProposal;
  receivedAt: string;
  exportedAt?: string;
  runId: string;
}
export interface AutoSyncStatus {
  config: AutoSyncConfig;
  ingestBaseUrl: string;
  pending: AutoSyncPending[];
  runs: Array<{ runId: string; startedAt: string; finishedAt: string; summary: AutoSyncRunSummary }>;
}

export const api = {
  me: () => request<MeResponse>('/me'),

  bootstrap: (adminGroupId: string) =>
    request<{ ok: true }>('/bootstrap', { method: 'POST', body: JSON.stringify({ adminGroupId }) }),

  getDirectory: () =>
    request<{ users: DirectoryUser[]; queues: DirectoryQueue[]; contacts: DirectoryContact[] }>('/directory'),
  addContact: (c: { displayName: string; phoneNumber: string; notes?: string; friendlyName?: string }) =>
    request<DirectoryContact>('/directory/contacts', { method: 'POST', body: JSON.stringify(c) }),

  // Server-side location-authorized: Teams users at the locations the caller may
  // see (own office location + group-mapped locations; admins see all). Includes
  // users without a PSTN number — they're reachable via an internal Teams call.
  // `queues` are the shared call-queue / auto-attendant dial endpoints (by DID).
  getNumbers: () => request<{ users: DirectoryUser[]; queues: DirectoryQueue[] }>('/numbers'),
  // Admin-only: manage which Entra groups may view which office locations.
  listNumbersAccess: () => request<NumbersLocationAccess[]>('/numbers-access'),
  putNumbersAccess: (id: string, body: NumbersLocationAccessInput) =>
    request<NumbersLocationAccess>(`/numbers-access/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteNumbersAccess: (id: string) =>
    request<void>(`/numbers-access/${id}`, { method: 'DELETE' }),
  // Admin-only: the tenant-wide hidden list + rules for the Numbers tab.
  getNumbersHidden: () => request<NumbersHidden>('/numbers-hidden'),
  putNumbersHidden: (body: NumbersHidden) =>
    request<NumbersHidden>('/numbers-hidden', { method: 'PUT', body: JSON.stringify(body) }),
  // Admin-only: friendly display-name overrides (tenant-wide + per-site).
  getNumbersFriendly: () => request<NumbersFriendlyNames>('/numbers-friendly'),
  putNumbersFriendly: (body: NumbersFriendlyNames) =>
    request<NumbersFriendlyNames>('/numbers-friendly', { method: 'PUT', body: JSON.stringify(body) }),

  getBranding: () => request<Branding>('/branding'),
  putBranding: (b: BrandingUpdate) =>
    request<Branding>('/branding', { method: 'PUT', body: JSON.stringify(b) }),
  /** Unauthenticated: resolves a company address before anyone signs in. */
  getPublicTenant: async (slug: string): Promise<PublicTenant> => {
    const res = await fetch(`${API_BASE}/public/tenant/${encodeURIComponent(slug)}`);
    if (!res.ok) {
      let body: any;
      try { body = await res.json(); } catch { /* not json */ }
      throw new ApiError(res.status, body?.error || res.statusText, body);
    }
    return res.json();
  },
  uploadLogo: async (file: File): Promise<{ logoBlobUrl: string }> => {
    const token = await getAuthToken();
    const fd = new FormData();
    fd.append('logo', file);
    const res = await fetch(`${API_BASE}/branding/logo`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    if (!res.ok) throw new ApiError(res.status, await res.text());
    return res.json();
  },

  listGroups: () => request<Group[]>('/groups'),
  putGroupAssignment: (groupId: string, body: { displayName: string; contactIds: string[] }) =>
    request<Group>(`/groups/${groupId}`, { method: 'PUT', body: JSON.stringify(body) }),

  getEffective: (userId: string = 'me') =>
    request<{ entries: EffectiveEntry[]; resolvedAt: string }>(`/users/${userId}/effective`),
  putUserAssignment: (userId: string, contactIds: string[]) =>
    request(`/users/${userId}/assignment`, { method: 'PUT', body: JSON.stringify({ contactIds }) }),

  // Members of a security group (queue agents), with their phone numbers.
  getGroupMembers: (groupId: string) => request<GroupMember[]>(`/groups/${groupId}/members`),
  // Name/mail/kind of any group (M365 or security) — shared-voicemail labels.
  getGroupInfo: (groupId: string) => request<GroupInfo>(`/groups/${groupId}/info`),
  getPresence: (ids: string[]) =>
    request<PresenceResponse>('/presence', { method: 'POST', body: JSON.stringify({ ids }) }),

  getAutoSync: () => request<AutoSyncStatus>('/auto-sync'),
  putAutoSync: (body: { enabled?: boolean; mode?: AutoSyncMode }) =>
    request<AutoSyncConfig>('/auto-sync', { method: 'PUT', body: JSON.stringify(body) }),
  rotateAutoSyncSecret: () =>
    request<{ secret: string; secretHint: string; secretCreatedAt: string }>('/auto-sync/secret', { method: 'POST' }),
  dismissAutoSyncPending: (locationId: string) =>
    request<void>(`/auto-sync/pending/${encodeURIComponent(locationId)}/dismiss`, { method: 'POST' }),

  // Audio assets (prompts, hold music). Playback URLs are short-lived SAS links
  // fetched on first play — an <audio> element cannot send a bearer token.
  getCallFlowAudioUrl: (audioId: string) =>
    request<{ url: string; expiresAt: string }>(`/call-flows-audio/${encodeURIComponent(audioId)}/url`),
  listCallFlowAudio: () => request<AudioAssetMeta[]>('/call-flows-audio'),
  deleteCallFlowAudio: (audioId: string) =>
    request<void>(`/call-flows-audio/${encodeURIComponent(audioId)}`, { method: 'DELETE' }),
  // Admin-only multipart upload. A teamsAudioFileId links the recording to every
  // prompt that names it (and replaces an earlier copy of the same recording).
  uploadCallFlowAudio: async (file: File, meta: { teamsAudioFileId?: string; teamsApplicationId?: string; fileName?: string } = {}): Promise<AudioAssetMeta> => {
    const token = await getAuthToken();
    const fd = new FormData();
    fd.append('file', file);
    if (meta.teamsAudioFileId) fd.append('teamsAudioFileId', meta.teamsAudioFileId);
    if (meta.teamsApplicationId) fd.append('teamsApplicationId', meta.teamsApplicationId);
    if (meta.fileName) fd.append('fileName', meta.fileName);
    const res = await fetch(`${API_BASE}/call-flows-audio`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
    if (!res.ok) {
      let body: any;
      try { body = await res.json(); } catch {}
      throw new ApiError(res.status, body?.error || res.statusText, body);
    }
    return res.json();
  },

  // Server-side group-filtered: returns only locations the caller may view
  listCallFlows: () => request<CallFlow[]>('/call-flows'),
  putCallFlow: (locationId: string, body: CallFlowInput) =>
    request<CallFlow>(`/call-flows/${locationId}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteCallFlow: (locationId: string) =>
    request<void>(`/call-flows/${locationId}`, { method: 'DELETE' }),
  importTeamsCallFlows: (exportJson: unknown) =>
    request<ImportResult>('/call-flows-import', {
      method: 'POST', body: JSON.stringify(exportJson),
    }),

  getPersonal: () => request<PersonalData>('/me/personal'),
  putPersonal: (data: PersonalData) =>
    request<PersonalData>('/me/personal', { method: 'PUT', body: JSON.stringify(data) }),

  getAuditLog: (params: { since?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.since) q.set('since', params.since);
    if (params.limit) q.set('limit', String(params.limit));
    return request<AuditEntry[]>(`/audit?${q}`);
  },

  requestAccess: () =>
    request<{ status: 'allowed' | 'pending' }>('/access-request', { method: 'POST' }),

  downloadAppPackage: async (): Promise<Blob> => {
    const token = await getAuthToken();
    const res = await fetch(`${API_BASE}/app-package`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      let body: any;
      try { body = await res.json(); } catch {}
      throw new ApiError(res.status, body?.error || res.statusText, body);
    }
    return res.blob();
  },

  operatorOverview: () => request<OperatorOverview>('/operator/tenants'),
  operatorDecide: (tenantId: string, action: 'approve' | 'deny') =>
    request<TenantAccessRow>(`/operator/tenants/${tenantId}`, {
      method: 'POST', body: JSON.stringify({ action }),
    }),
};
